import React from "react";
import { Box, Button, Tooltip } from "@mui/material";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { InfoOutlined } from "@mui/icons-material";
import GetAnyHelpModalContext from "../../contexts/GetAnyHelpModalContext";
import { useLocation } from "react-router-dom";
import { useJourneyStatus } from "./hooks/useJourneyStatus";
import {
  TutorialJourneyStatus,
  TutorialJourneyType,
} from "@growth-machine-llc/stridist-api-client";
import { getJourneyTypeForPath } from "../../routes/admin/utils";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { JourneyAvailabilityContext } from "../../contexts/JourneyAvailabilityContext";
import { useMediaMobile } from "../../hooks/useMediaMobile";
import { sharedLabelAnimationSx } from "./AppDrawer";

const sharedButtonSx = (minimized: boolean, fullWidth: boolean) => ({
  ...(fullWidth
    ? {
        width: "100%",
        justifyContent: "center",
        paddingInline: 0,
      }
    : {
        minWidth: minimized ? 40 : 120,
      }),

  display: "flex",
  alignItems: "center",
  position: "relative",

  "& .jl-label": {
    position: minimized ? "absolute" : "static",
    left: minimized ? "50%" : "auto",
    whiteSpace: "nowrap",
    pointerEvents: "none",

    ...sharedLabelAnimationSx(minimized),

    ...(minimized && {
      width: 0,
      height: 0,
      overflow: "hidden",
    }),

    transform: minimized
      ? "translate(-50%, 0) translateX(-8px)"
      : "translate(0, 0)",
  },

  transition: `min-width 300ms ease 200ms`,
});

export interface JourneyLauncherProps {
  wrapperClassName?: string;
  buttonClassName?: string;
  fullWidth?: boolean;
  minimized?: boolean;
  onStart?: () => void;
}

export const JourneyLauncher: React.FC<JourneyLauncherProps> = ({
  wrapperClassName,
  buttonClassName,
  fullWidth = false,
  minimized = false,
  onStart,
}) => {
  const getHelpContext = React.useContext(GetAnyHelpModalContext);
  const { setGetAnyHelpModal } = getHelpContext;
  const { enabled, disabledReason } = React.useContext(
    JourneyAvailabilityContext,
  );

  const { pathname } = useLocation();
  const user = useCurrentUser();
  const isMobile = useMediaMobile("md", false);

  const journeyType = React.useMemo(
    () => getJourneyTypeForPath(pathname),
    [pathname],
  );
  const journeyStatus = useJourneyStatus(journeyType as TutorialJourneyType);

  const isJourneyAvailable = React.useMemo(
    () => !!journeyType && journeyStatus !== undefined,
    [journeyType, journeyStatus],
  );

  const shouldHideBackButton =
    journeyStatus === TutorialJourneyStatus.DRAFT && !user?.admin;

  if (!isJourneyAvailable || shouldHideBackButton || isMobile) {
    return <Box className={wrapperClassName}/>;
  }

  return (
    <Box className={wrapperClassName}>
      <Tooltip
        title={!enabled && disabledReason ? disabledReason : ""}
        placement="top"
      >
        <span>
          <Button
            className={buttonClassName}
            size="small"
            startIcon={<LightbulbOutlinedIcon fontSize="small" />}
            onClick={onStart}
            fullWidth={fullWidth}
            disabled={!enabled}
            sx={sharedButtonSx(minimized, fullWidth)}
          >
            <span className="jl-label">Tips & Tricks</span>
          </Button>
        </span>
      </Tooltip>

      <Button
        className={buttonClassName}
        size="small"
        startIcon={<InfoOutlined fontSize="small" />}
        onClick={() => setGetAnyHelpModal(true)}
        fullWidth={fullWidth}
        sx={sharedButtonSx(minimized, fullWidth)}
      >
        <span className="jl-label">Help</span>
      </Button>
    </Box>
  );
};
